import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Form,
  Header,
  Icon,
  Label,
  Message,
  Modal,
  Pagination,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';
import {
  BillingDiscountActionV1021,
  discountErrorV1021,
  loadDiscountPagesV1021,
} from './BillingDiscountLifecycleV1021';
import {
  BILLING_DISCOUNT_EFFECTIVE_NEXT_V816,
  BILLING_DISCOUNT_EFFECTIVE_NOW_V1022,
  BILLING_DISCOUNT_MODE_FIXED_V816,
  BILLING_DISCOUNT_MODE_TIERED_V816,
  buildDiscountProgramRequestV816,
  payablePercentToBasisPointsV816,
  scheduleModeV816,
} from './billingDiscountProgramV816';
import { discountTimelineV1023 } from './billingDiscountTimelineV1023';

const tierClientId = () =>
  `${Date.now()}:${Math.random().toString(16).slice(2)}`;

const emptyTier = (threshold = '', payable = '', name = '') => ({
  client_id: tierClientId(),
  threshold_cny: threshold,
  payable_percent: payable,
  display_name: name,
});

const emptyForm = (name = '') => ({
  name,
  mode: BILLING_DISCOUNT_MODE_FIXED_V816,
  threshold_basis: 'pre_discount_cny',
  note: '',
  fixed_payable_percent: '100',
  tiers: [emptyTier('0', '100'), emptyTier('', '')],
});

const requestIdempotencyKey = () => {
  if (window.crypto?.randomUUID)
    return `discount:${window.crypto.randomUUID()}`;
  return `discount:${Date.now()}:${Math.random().toString(16).slice(2)}`;
};

const BillingDiscountProgramSettingV816 = ({
  accountId,
  organizationName = '',
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(() => emptyForm());
  const [templates, setTemplates] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [initialScheduleId, setInitialScheduleId] = useState('');
  const [ongoingScheduleId, setOngoingScheduleId] = useState('');
  const [effectiveMode, setEffectiveMode] = useState(
    BILLING_DISCOUNT_EFFECTIVE_NOW_V1022
  );
  const [trialEnabled, setTrialEnabled] = useState(false);
  const [trialCycles, setTrialCycles] = useState(1);
  const [assignments, setAssignments] = useState([]);
  const [lastProgram, setLastProgram] = useState(null);
  const [libraryState, setLibraryState] = useState('active');
  const [libraryPage, setLibraryPage] = useState(1);
  const [timelinePage, setTimelinePage] = useState(1);
  const [history, setHistory] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [immediateDraft, setImmediateDraft] = useState(null);
  const [applyError, setApplyError] = useState('');
  const applyBusy = useRef(false);
  const applyEpoch = useRef(0);
  const requestEpoch = useRef(0);
  const assignmentEpoch = useRef(0);
  const activeAccount = useRef(accountId);
  activeAccount.current = accountId;
  const visibleSchedules = schedules.filter(
    (s) => (s.library_state || 'active') === libraryState
  );
  const timeline = discountTimelineV1023(assignments);

  const basisOptions = useMemo(
    () => [
      {
        key: 'pre_discount_cny',
        value: 'pre_discount_cny',
        text: t('setting.billing.basis.pre_discount'),
      },
      {
        key: 'net_paid_cny',
        value: 'net_paid_cny',
        text: t('setting.billing.basis.net_paid'),
      },
    ],
    [t]
  );

  const scheduleOptions = useMemo(
    () =>
      schedules
        .filter((s) => !s.library_state || s.library_state === 'active')
        .map((schedule) => ({
          key: schedule.id,
          value: schedule.id,
          text: `${t(
            `setting.billing.program.mode.${scheduleModeV816(schedule)}`
          )} · ${schedule.name} v${schedule.version}`,
        })),
    [schedules, t]
  );

  const loadBaseData = useCallback(async () => {
    const epoch = ++requestEpoch.current;
    setLoading(true);
    try {
      const [templateResponse, scheduleItems] = await Promise.all([
        API.get('/api/billing/v8/admin/discount-schedule-templates'),
        loadDiscountPagesV1021(
          '/api/billing/v8/admin/discount-schedules?state=all'
        ),
      ]);
      if (epoch !== requestEpoch.current) return;
      if (templateResponse?.data?.success) {
        setTemplates(templateResponse.data.data || []);
      }
      setSchedules(scheduleItems);
      const active = new Set(
        scheduleItems
          .filter((s) => s.library_state === 'active' || !s.library_state)
          .map((s) => s.id)
      );
      setInitialScheduleId((id) => (active.has(id) ? id : ''));
      setOngoingScheduleId((id) => (active.has(id) ? id : ''));
    } catch (error) {
      if (epoch === requestEpoch.current)
        setLoadError(discountErrorV1021(error, t));
      throw error;
    } finally {
      if (epoch === requestEpoch.current) setLoading(false);
    }
  }, [t]);

  const loadAssignments = useCallback(async () => {
    const epoch = ++assignmentEpoch.current;
    if (!accountId) {
      setAssignments([]);
      return;
    }
    try {
      const [items, changes] = await Promise.all([
        loadDiscountPagesV1021(
          `/api/billing/v8/admin/accounts/${accountId}/discount-assignments`
        ),
        loadDiscountPagesV1021(
          `/api/billing/v8/admin/accounts/${accountId}/discount-history`
        ),
      ]);
      if (
        activeAccount.current !== accountId ||
        epoch !== assignmentEpoch.current
      )
        return;
      setAssignments(items);
      setHistory(changes);
    } catch (error) {
      if (
        activeAccount.current === accountId &&
        epoch === assignmentEpoch.current
      )
        setLoadError(discountErrorV1021(error, t));
      throw error;
    }
  }, [accountId, t]);

  useEffect(() => {
    loadBaseData().catch(() => {});
    return () => {
      requestEpoch.current++;
    };
  }, [loadBaseData]);

  useEffect(() => {
    loadAssignments().catch(() => {});
    return () => {
      assignmentEpoch.current++;
    };
  }, [loadAssignments]);

  useEffect(() => {
    applyEpoch.current++;
    return () => {
      applyEpoch.current++;
    };
  }, [accountId]);

  useEffect(() => {
    setForm(
      emptyForm(
        t('setting.billing.program.default_fixed_name', {
          name: organizationName,
        })
      )
    );
    setAssignments([]);
    setLastProgram(null);
    setInitialScheduleId('');
    setOngoingScheduleId('');
    setEffectiveMode(BILLING_DISCOUNT_EFFECTIVE_NOW_V1022);
    setTrialEnabled(false);
    setTrialCycles(1);
    setHistory([]);
    setLoadError('');
    setTimelinePage(1);
    setLibraryPage(1);
    setImmediateDraft(null);
    setApplyError('');
    applyBusy.current = false;
    setLoading(false);
  }, [accountId, organizationName, t]);

  const refreshLifecycle = async () => {
    setSelectedSchedule(null);
    setLastProgram(null);
    try {
      await Promise.all([loadAssignments(), loadBaseData()]);
      setLoadError('');
    } catch (_) {
      // The mutation is already committed; keep stale controls disabled until
      // a successful refresh, rather than reporting a second failed mutation.
    }
  };

  const applyTemplate = (template) => {
    const templateTiers = (template.tiers || []).map((tier) => ({
      client_id: tierClientId(),
      threshold_cny: tier.threshold_cny,
      payable_percent: tier.payable_percent,
      display_name: tier.display_name,
    }));
    const mode =
      templateTiers.length === 1
        ? BILLING_DISCOUNT_MODE_FIXED_V816
        : BILLING_DISCOUNT_MODE_TIERED_V816;
    setForm({
      name: template.name,
      mode,
      threshold_basis: template.threshold_basis,
      note: template.note || '',
      fixed_payable_percent: templateTiers[0]?.payable_percent || '100',
      tiers: templateTiers,
    });
  };

  const changeMode = (mode) => {
    setForm((current) => ({
      ...current,
      mode,
      name:
        current.name ||
        t(
          mode === BILLING_DISCOUNT_MODE_FIXED_V816
            ? 'setting.billing.program.default_fixed_name'
            : 'setting.billing.program.default_tiered_name',
          { name: organizationName }
        ),
    }));
  };

  const updateTier = (index, field, value) => {
    setForm((current) => ({
      ...current,
      tiers: current.tiers.map((tier, tierIndex) =>
        tierIndex === index ? { ...tier, [field]: value } : tier
      ),
    }));
  };

  const removeTier = (index) => {
    setForm((current) => ({
      ...current,
      tiers: current.tiers.filter((_, tierIndex) => tierIndex !== index),
    }));
  };

  const createPlan = async () => {
    const name = String(form.name).trim();
    let tiers;
    if (form.mode === BILLING_DISCOUNT_MODE_FIXED_V816) {
      const payableBasisPoints = payablePercentToBasisPointsV816(
        form.fixed_payable_percent
      );
      tiers = [
        {
          threshold_cny: '0',
          payable_basis_points: payableBasisPoints,
          display_name: '',
        },
      ];
    } else {
      tiers = form.tiers.map((tier) => ({
        threshold_cny: String(tier.threshold_cny).trim(),
        payable_basis_points: payablePercentToBasisPointsV816(
          tier.payable_percent
        ),
        display_name: String(tier.display_name).trim(),
      }));
    }
    if (
      !name ||
      tiers.some(
        (tier) => !tier.threshold_cny || tier.payable_basis_points === null
      ) ||
      (form.mode === BILLING_DISCOUNT_MODE_TIERED_V816 && tiers.length < 2)
    ) {
      showError(t('setting.billing.errors.invalid_plan'));
      return;
    }
    setLoading(true);
    try {
      const response = await API.post('/api/billing/v8/admin/discount-plans', {
        name,
        mode: form.mode,
        threshold_basis: form.threshold_basis,
        note: String(form.note).trim(),
        tiers,
      });
      if (!response?.data?.success) return;
      setSelectedSchedule(response.data.data);
      setInitialScheduleId(response.data.data.id);
      showSuccess(t('setting.billing.messages.plan_created'));
      await loadBaseData();
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          error?.message ||
          t('setting.billing.errors.create')
      );
    } finally {
      setLoading(false);
    }
  };

  const inspectSchedule = async (scheduleId) => {
    setLoading(true);
    try {
      const response = await API.get(
        `/api/billing/v8/admin/discount-schedules/${scheduleId}`
      );
      if (response?.data?.success) setSelectedSchedule(response.data.data);
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          error?.message ||
          t('setting.billing.errors.load')
      );
    } finally {
      setLoading(false);
    }
  };

  const assignProgram = async () => {
    if (loading || loadError || applyBusy.current) return;
    const current = timeline.current;
    const payload = buildDiscountProgramRequestV816({
      initialScheduleId,
      ongoingScheduleId,
      effectiveMode,
      trialEnabled,
      trialCycles,
      idempotencyKey: requestIdempotencyKey(),
      expectedAssignmentId: current?.id || '0',
      expectedRevision: current?.revision || 0,
    });
    if (!accountId || !payload) {
      showError(t('setting.billing.errors.program_required'));
      return;
    }
    if (effectiveMode === BILLING_DISCOUNT_EFFECTIVE_NOW_V1022) {
      setApplyError('');
      setImmediateDraft({
        payload,
        accountId,
        epoch: applyEpoch.current,
        before:
          current?.schedule?.name ||
          t('setting.billing.immediate.default_rate'),
        after: schedules.find((s) => s.id === initialScheduleId)?.name,
        ongoing: trialEnabled
          ? schedules.find((s) => s.id === ongoingScheduleId)?.name
          : '',
        pending: timeline.rows.filter((row) => row.status === 'pending'),
      });
      return;
    }
    await submitProgram(payload, accountId, applyEpoch.current, false);
  };

  const submitProgram = async (payload, targetAccount, epoch, immediate) => {
    if (applyBusy.current) return;
    applyBusy.current = true;
    setLoading(true);
    try {
      const response = await API.post(
        `/api/billing/v8/admin/accounts/${targetAccount}/discount-programs`,
        payload,
        { validateStatus: () => true }
      );
      if (!response?.data?.success)
        throw new Error(
          response?.data?.code ||
            response?.data?.message ||
            t('setting.billing.errors.load')
        );
      if (
        epoch !== applyEpoch.current ||
        activeAccount.current !== targetAccount
      )
        return;
      setImmediateDraft(null);
      setApplyError('');
      setLastProgram(response.data.data);
      showSuccess(
        t(
          immediate
            ? 'setting.billing.immediate.saved'
            : payload.trial_cycles > 0
            ? 'setting.billing.messages.trial_program_assigned'
            : 'setting.billing.messages.program_assigned'
        )
      );
      await Promise.all([loadAssignments(), loadBaseData()]);
    } catch (error) {
      if (
        epoch !== applyEpoch.current ||
        activeAccount.current !== targetAccount
      )
        return;
      if (immediate) setApplyError(discountErrorV1021(error, t));
      else showError(discountErrorV1021(error, t));
    } finally {
      if (epoch === applyEpoch.current) {
        applyBusy.current = false;
        setLoading(false);
      }
    }
  };

  return (
    <Form loading={loading} className='discount-program-v1021'>
      {loadError && (
        <Message negative role='alert'>
          <p>{loadError}</p>
          <Button
            type='button'
            icon='refresh'
            content={t('setting.billing.lifecycle.retry')}
            onClick={refreshLifecycle}
          />
        </Message>
      )}
      {organizationName ? <Message info content={organizationName} /> : null}
      <Header as='h3'>{t('setting.billing.program.editor_title')}</Header>

      <Button.Group size='small'>
        <Button
          type='button'
          active={form.mode === BILLING_DISCOUNT_MODE_FIXED_V816}
          onClick={() => changeMode(BILLING_DISCOUNT_MODE_FIXED_V816)}
        >
          <Icon name='percent' />
          {t('setting.billing.program.mode.fixed')}
        </Button>
        <Button
          type='button'
          active={form.mode === BILLING_DISCOUNT_MODE_TIERED_V816}
          onClick={() => changeMode(BILLING_DISCOUNT_MODE_TIERED_V816)}
        >
          <Icon name='signal' />
          {t('setting.billing.program.mode.tiered')}
        </Button>
      </Button.Group>

      {templates.length > 0 ? (
        <Button.Group size='small' style={{ marginLeft: 8 }}>
          {templates.map((template) => (
            <Button
              type='button'
              key={template.template_id}
              icon
              labelPosition='left'
              onClick={() => applyTemplate(template)}
            >
              <Icon name='magic' />
              {t('setting.billing.editor.load_template', {
                name: template.name,
              })}
            </Button>
          ))}
        </Button.Group>
      ) : null}

      <Form.Group widths='equal' style={{ marginTop: 16 }}>
        <Form.Input
          label={t('setting.billing.program.plan_name')}
          value={form.name}
          maxLength={191}
          onChange={(_, { value }) =>
            setForm((current) => ({ ...current, name: value }))
          }
        />
        {form.mode === BILLING_DISCOUNT_MODE_TIERED_V816 ? (
          <Form.Field>
            <label>{t('setting.billing.fields.threshold_basis')}</label>
            <Dropdown
              selection
              fluid
              options={basisOptions}
              value={form.threshold_basis}
              onChange={(_, { value }) =>
                setForm((current) => ({
                  ...current,
                  threshold_basis: value,
                }))
              }
            />
          </Form.Field>
        ) : (
          <Form.Input
            label={t('setting.billing.program.fixed_payable_percent')}
            type='text'
            inputMode='decimal'
            value={form.fixed_payable_percent}
            onChange={(_, { value }) =>
              setForm((current) => ({
                ...current,
                fixed_payable_percent: value,
              }))
            }
          />
        )}
      </Form.Group>
      <Form.TextArea
        label={t('setting.billing.fields.note')}
        value={form.note}
        maxLength={1000}
        onChange={(_, { value }) =>
          setForm((current) => ({ ...current, note: value }))
        }
      />

      {form.mode === BILLING_DISCOUNT_MODE_TIERED_V816 ? (
        <>
          <div style={{ overflowX: 'auto' }}>
            <Table compact unstackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    {t('setting.billing.fields.threshold_cny')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('setting.billing.fields.payable_percent')}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {t('setting.billing.program.tier_name_optional')}
                  </Table.HeaderCell>
                  <Table.HeaderCell collapsing />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {form.tiers.map((tier, index) => (
                  <Table.Row key={tier.client_id}>
                    <Table.Cell>
                      <Form.Input
                        type='text'
                        inputMode='decimal'
                        value={tier.threshold_cny}
                        onChange={(_, { value }) =>
                          updateTier(index, 'threshold_cny', value)
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Form.Input
                        type='text'
                        inputMode='decimal'
                        value={tier.payable_percent}
                        label='%'
                        labelPosition='right'
                        onChange={(_, { value }) =>
                          updateTier(index, 'payable_percent', value)
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Form.Input
                        value={tier.display_name}
                        maxLength={191}
                        onChange={(_, { value }) =>
                          updateTier(index, 'display_name', value)
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        type='button'
                        icon='trash'
                        basic
                        negative
                        disabled={form.tiers.length <= 2}
                        title={t('setting.billing.actions.remove_tier')}
                        aria-label={t('setting.billing.actions.remove_tier')}
                        onClick={() => removeTier(index)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
          <Button
            type='button'
            icon
            labelPosition='left'
            onClick={() =>
              setForm((current) => ({
                ...current,
                tiers: [...current.tiers, emptyTier()],
              }))
            }
          >
            <Icon name='plus' />
            {t('setting.billing.actions.add_tier')}
          </Button>
        </>
      ) : null}

      <Button
        type='button'
        primary
        icon
        labelPosition='left'
        onClick={createPlan}
      >
        <Icon name='save' />
        {t('setting.billing.program.save_plan')}
      </Button>

      <Divider />
      <Header as='h3'>{t('setting.billing.program.library_title')}</Header>
      <div className='discount-library-toolbar-v1021'>
        <Button.Group size='small'>
          {['active', 'archived'].map((state) => (
            <Button
              key={state}
              type='button'
              active={libraryState === state}
              onClick={() => {
                setLibraryState(state);
                setLibraryPage(1);
              }}
            >
              {t(`setting.billing.lifecycle.library.${state}`)}
            </Button>
          ))}
        </Button.Group>
      </div>
      <div className='discount-table-v1021'>
        <Table compact selectable unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {t('setting.billing.program.plan_name')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.program.mode_label')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.fields.version')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.schedules.tiers')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.schedules.accounts')}
              </Table.HeaderCell>
              <Table.HeaderCell collapsing />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleSchedules
              .slice(
                (Math.min(
                  libraryPage,
                  Math.max(1, Math.ceil(visibleSchedules.length / 20))
                ) -
                  1) *
                  20,
                Math.min(
                  libraryPage,
                  Math.max(1, Math.ceil(visibleSchedules.length / 20))
                ) * 20
              )
              .map((schedule) => (
                <Table.Row key={schedule.id}>
                  <Table.Cell>{schedule.name}</Table.Cell>
                  <Table.Cell>
                    {t(
                      `setting.billing.program.mode.${scheduleModeV816(
                        schedule
                      )}`
                    )}
                  </Table.Cell>
                  <Table.Cell>{schedule.version}</Table.Cell>
                  <Table.Cell>{schedule.tier_count}</Table.Cell>
                  <Table.Cell>{schedule.assignment_count}</Table.Cell>
                  <Table.Cell>
                    <div className='discount-row-actions-v1021'>
                      <Button
                        type='button'
                        icon='eye'
                        basic
                        title={t('setting.billing.actions.inspect')}
                        aria-label={t('setting.billing.actions.inspect')}
                        onClick={() => inspectSchedule(schedule.id)}
                      />
                      <BillingDiscountActionV1021
                        action={
                          libraryState === 'archived' ? 'restore' : 'archive'
                        }
                        record={schedule}
                        accountId={accountId}
                        disabled={!!loadError}
                        onChanged={refreshLifecycle}
                      />
                      {schedule.can_delete && (
                        <BillingDiscountActionV1021
                          action='delete'
                          record={schedule}
                          accountId={accountId}
                          disabled={!!loadError}
                          onChanged={refreshLifecycle}
                        />
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            {visibleSchedules.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={6}>
                  {t('setting.billing.lifecycle.empty_library')}
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
        {visibleSchedules.length > 20 && (
          <Pagination
            size='mini'
            activePage={Math.min(
              libraryPage,
              Math.ceil(visibleSchedules.length / 20)
            )}
            totalPages={Math.ceil(visibleSchedules.length / 20)}
            onPageChange={(_, { activePage }) => setLibraryPage(activePage)}
          />
        )}
      </div>
      {selectedSchedule ? (
        <Message>
          <Message.Header>{`${selectedSchedule.name} v${selectedSchedule.version}`}</Message.Header>
          <Message.List>
            {(selectedSchedule.tiers || []).map((tier) => (
              <Message.Item
                key={
                  tier.id ||
                  `${tier.threshold_cny}-${tier.payable_basis_points}`
                }
              >
                {tier.display_name}: CNY {tier.threshold_cny} /{' '}
                {tier.payable_percent}%
              </Message.Item>
            ))}
          </Message.List>
        </Message>
      ) : null}

      <Divider />
      <Header as='h3'>{t('setting.billing.program.assignment_title')}</Header>
      <Form.Field>
        <label>{t('setting.billing.program.effective_mode')}</label>
        <Button.Group
          size='small'
          className='discount-effective-v1022'
          aria-label={t('setting.billing.program.effective_mode')}
        >
          <Button
            type='button'
            toggle
            active={effectiveMode === BILLING_DISCOUNT_EFFECTIVE_NOW_V1022}
            onClick={() =>
              setEffectiveMode(BILLING_DISCOUNT_EFFECTIVE_NOW_V1022)
            }
          >
            {t('setting.billing.immediate.label')}
          </Button>
          <Button
            type='button'
            toggle
            active={effectiveMode === BILLING_DISCOUNT_EFFECTIVE_NEXT_V816}
            onClick={() =>
              setEffectiveMode(BILLING_DISCOUNT_EFFECTIVE_NEXT_V816)
            }
          >
            {t('setting.billing.program.effective.next_cycle')}
          </Button>
        </Button.Group>
      </Form.Field>

      <Form.Field>
        <Checkbox
          toggle
          checked={trialEnabled}
          label={t('setting.billing.program.trial_enabled')}
          onChange={(_, { checked }) => setTrialEnabled(checked)}
        />
      </Form.Field>

      <Form.Group widths='equal'>
        <Form.Field>
          <label>
            {t(
              trialEnabled
                ? 'setting.billing.program.trial_plan'
                : 'setting.billing.program.active_plan'
            )}
          </label>
          <Dropdown
            selection
            search
            fluid
            options={scheduleOptions}
            value={initialScheduleId}
            aria-label={t('setting.billing.program.active_plan')}
            onChange={(_, { value }) => setInitialScheduleId(value)}
          />
        </Form.Field>
        {trialEnabled ? (
          <>
            <Form.Input
              label={t('setting.billing.program.trial_cycles')}
              type='number'
              min='1'
              max='24'
              step='1'
              value={trialCycles}
              onChange={(_, { value }) => setTrialCycles(value)}
            />
            <Form.Field>
              <label>{t('setting.billing.program.ongoing_plan')}</label>
              <Dropdown
                selection
                search
                fluid
                options={scheduleOptions}
                value={ongoingScheduleId}
                onChange={(_, { value }) => setOngoingScheduleId(value)}
              />
            </Form.Field>
          </>
        ) : null}
      </Form.Group>

      <Button
        type='button'
        primary
        icon
        labelPosition='left'
        onClick={assignProgram}
        disabled={loading || !!loadError || !initialScheduleId}
      >
        <Icon
          name={
            effectiveMode === BILLING_DISCOUNT_EFFECTIVE_NOW_V1022
              ? 'bolt'
              : 'calendar plus'
          }
        />
        {t('setting.billing.program.apply_program')}
      </Button>

      <Modal
        open={!!immediateDraft}
        onClose={() => {
          if (!applyBusy.current) setImmediateDraft(null);
        }}
        closeOnDimmerClick={!loading}
        closeOnEscape={!loading}
        size='small'
        className='discount-modal-v1021'
        aria-labelledby='discount-immediate-title'
      >
        <Modal.Header id='discount-immediate-title'>
          {t('setting.billing.immediate.title')}
        </Modal.Header>
        <Modal.Content>
          <div className='discount-confirm-name-v1021'>
            {immediateDraft?.before} {' -> '} {immediateDraft?.after}
          </div>
          <p>{t('setting.billing.immediate.confirm')}</p>
          {immediateDraft?.ongoing && (
            <p>
              {t('setting.billing.immediate.trial', {
                cycles: immediateDraft.payload.trial_cycles,
                name: immediateDraft.ongoing,
              })}
            </p>
          )}
          {!!immediateDraft?.pending.length && (
            <Message warning>
              <Message.Header>
                {t('setting.billing.immediate.pending')}
              </Message.Header>
              <Message.List
                items={immediateDraft.pending.map(
                  (row) =>
                    `${row.schedule.name}: ${row.effective_period_start_iso}`
                )}
              />
            </Message>
          )}
          {applyError && (
            <Message negative role='alert'>
              {applyError}
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            type='button'
            disabled={loading}
            onClick={() => setImmediateDraft(null)}
          >
            {t('setting.billing.lifecycle.back')}
          </Button>
          {applyError && (
            <Button
              type='button'
              disabled={loading}
              onClick={async () => {
                setImmediateDraft(null);
                await refreshLifecycle();
              }}
            >
              {t('setting.billing.immediate.refresh')}
            </Button>
          )}
          <Button
            type='button'
            primary
            loading={loading}
            disabled={loading}
            onClick={() =>
              submitProgram(
                immediateDraft.payload,
                immediateDraft.accountId,
                immediateDraft.epoch,
                true
              )
            }
          >
            {t('setting.billing.immediate.confirm_action')}
          </Button>
        </Modal.Actions>
      </Modal>

      {lastProgram ? (
        <Message positive style={{ marginTop: 16 }}>
          <Message.Header>
            {t('setting.billing.program.timeline_created')}
          </Message.Header>
          <Message.List>
            <Message.Item>
              {lastProgram.initial.schedule.name}:{' '}
              {lastProgram.initial.effective_period_start_iso}
            </Message.Item>
            {lastProgram.ongoing ? (
              <Message.Item>
                {lastProgram.ongoing.schedule.name}:{' '}
                {lastProgram.ongoing.effective_period_start_iso}
              </Message.Item>
            ) : null}
          </Message.List>
        </Message>
      ) : null}

      {assignments.length > 0 ? (
        <div className='discount-table-v1021' style={{ marginTop: 16 }}>
          <Table compact unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {t('setting.billing.program.timeline_plan')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('setting.billing.assignment.effective_at')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('setting.billing.assignment.status')}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {t('setting.billing.lifecycle.operations')}
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {timeline.rows
                .slice(
                  (Math.min(
                    timelinePage,
                    Math.max(1, Math.ceil(assignments.length / 20))
                  ) -
                    1) *
                    20,
                  Math.min(
                    timelinePage,
                    Math.max(1, Math.ceil(assignments.length / 20))
                  ) * 20
                )
                .map((assignment) => (
                  <Table.Row key={assignment.id}>
                    <Table.Cell>{`${assignment.schedule.name} v${assignment.schedule.version}`}</Table.Cell>
                    <Table.Cell>
                      {assignment.effective_period_start_iso}
                    </Table.Cell>
                    <Table.Cell>
                      <Label
                        basic
                        color={
                          assignment.status === 'pending'
                            ? 'blue'
                            : assignment.status === 'current'
                            ? 'green'
                            : undefined
                        }
                      >
                        {assignment.status === 'pending'
                          ? t('setting.billing.assignment.pending')
                          : t(
                              `setting.billing.assignment.${assignment.status}`
                            )}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      {assignment.can_manage && (
                        <div className='discount-row-actions-v1021'>
                          <BillingDiscountActionV1021
                            key={`${accountId}:${assignment.id}:replace`}
                            action='replace'
                            record={assignment}
                            accountId={accountId}
                            schedules={schedules}
                            disabled={!!loadError}
                            onChanged={refreshLifecycle}
                          />
                          <BillingDiscountActionV1021
                            key={`${accountId}:${assignment.id}:cancel`}
                            action='cancel'
                            record={assignment}
                            accountId={accountId}
                            disabled={!!loadError}
                            onChanged={refreshLifecycle}
                          />
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
            </Table.Body>
          </Table>
          {assignments.length > 20 && (
            <Pagination
              size='mini'
              activePage={Math.min(
                timelinePage,
                Math.ceil(assignments.length / 20)
              )}
              totalPages={Math.ceil(assignments.length / 20)}
              onPageChange={(_, { activePage }) => setTimelinePage(activePage)}
            />
          )}
        </div>
      ) : null}
      {history.length > 0 && (
        <details className='discount-history-v1021'>
          <summary>{t('setting.billing.lifecycle.history')}</summary>
          <ul>
            {history.map((item) => (
              <li key={item.id}>
                {t(`setting.billing.lifecycle.history_actions.${item.action}`)}:{' '}
                {item.schedule?.id === '0'
                  ? t('setting.billing.immediate.default_rate')
                  : `${item.schedule.name} v${item.schedule.version}`}
                {item.replacement_schedule &&
                  ` -> ${item.replacement_schedule.name} v${item.replacement_schedule.version}`}
                {' / '}
                {item.effective_period_start_iso}
                {' / '}
                {new Date(item.created_at * 1000).toLocaleString()}
                {' / '}
                {t('setting.billing.lifecycle.actor', {
                  id: item.actor_user_id,
                })}
              </li>
            ))}
          </ul>
        </details>
      )}
    </Form>
  );
};

export default BillingDiscountProgramSettingV816;
